// mail.ts — typed client for the Mail section (/api/mail/*).
//
// Like collab, /api/mail is token-gated: GET /enroll once for an httpOnly cookie,
// then fetch + EventSource carry it. Models use discriminated unions for channel
// and folder role; no `any` at the API boundary (Matt-Pocock standard, R23).

import { useCallback, useEffect, useRef, useState } from 'react'

// ── domain models ─────────────────────────────────────────────────────────────
export interface MailAccount {
  id: string
  address: string
  fromName?: string
  default?: boolean
}

export type FolderRole =
  | 'inbox'
  | 'sent'
  | 'drafts'
  | 'trash'
  | 'archive'
  | 'junk'
  | 'other'

export interface MailFolder {
  path: string
  name: string
  role: FolderRole
  specialUse: string | null
  subscribed?: boolean
}

export interface MailLabel {
  id: string
  name: string
  color: string
  show: boolean
}

export interface MailAddress {
  name: string
  address: string
}

export interface MessageSummary {
  uid: number
  seq: number
  subject: string
  from: MailAddress | null
  to: MailAddress[]
  date: string | null
  unseen: boolean
  starred?: boolean
  hasAttachment?: boolean
  // Present only in the unified "All" view: which mailbox/folder this came from,
  // so the reader can fetch the body from the right account.
  accountId?: string
  accountAddress?: string
  folder?: string
}

export type BulkAction = 'read' | 'unread' | 'star' | 'unstar' | 'archive' | 'trash' | 'move'

export interface BulkItem {
  accountId: string
  uid: number | string
  folder: string
}

export interface MailAccountError {
  accountId: string
  error: string
}

export interface AttachmentMeta {
  index: number
  filename: string
  contentType: string
  size: number
}

export interface MessageDetail {
  uid: number
  subject: string
  from: MailAddress | null
  to: MailAddress[]
  cc: MailAddress[]
  date: string | null
  text: string
  html: string
  collabSessionId: string | null
  attachments: AttachmentMeta[]
}

export interface MailAttachmentRef {
  sha256: string
  filename: string
  contentType: string
  size: number
  inline?: boolean
  cid?: string
}

export interface DraftSpec {
  to?: string
  cc?: string
  bcc?: string
  subject?: string
  text?: string
  html?: string
  collabSessionId?: string
  attachments?: MailAttachmentRef[]
}

export type RewriteEngine = 'claude' | 'agy'

export interface CollabSearchHit {
  id: string
  prompt_preview: string
  created_at: string | null
  has_synthesis: boolean
  score: number
}

// SSE events from /api/mail/events (discriminated union).
export type MailEvent =
  | { type: 'hello'; ok: true }
  | { type: 'counts'; counts: Record<string, number> }
  | { type: 'mail:new'; account: string; folder: string; count: number }

// ── enroll + fetch wrapper ────────────────────────────────────────────────────
let enrolled: Promise<void> | null = null
export function enrollMail(): Promise<void> {
  if (!enrolled) {
    enrolled = fetch('/api/mail/enroll', { cache: 'no-store' })
      .then(() => undefined)
      .catch(() => undefined)
  }
  return enrolled
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  await enrollMail()
  const r = await fetch('/api/mail' + path, init)
  if (!r.ok) {
    let msg = `HTTP ${r.status}`
    try {
      const j = (await r.json()) as { error?: string; message?: string }
      msg = j?.message || j?.error || msg
    } catch {
      /* keep status */
    }
    throw new Error(msg)
  }
  return r.json() as Promise<T>
}

const enc = encodeURIComponent

export function listAccounts(): Promise<{ ok: boolean; accounts: MailAccount[] }> {
  return api('/accounts')
}
export function listFolders(account: string): Promise<{ ok: boolean; folders: MailFolder[] }> {
  return api(`/${enc(account)}/folders`)
}
function folderOp(account: string, op: 'folders' | 'folders/rename' | 'folders/delete' | 'folders/subscribe', body: Record<string, unknown>): Promise<{ ok: boolean; path?: string }> {
  return api(`/${enc(account)}/${op}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
}
export const createFolder = (account: string, path: string) => folderOp(account, 'folders', { path })
export const renameFolder = (account: string, path: string, newPath: string) => folderOp(account, 'folders/rename', { path, newPath })
export const deleteFolder = (account: string, path: string) => folderOp(account, 'folders/delete', { path })
export const setFolderSubscribed = (account: string, path: string, subscribed: boolean) => folderOp(account, 'folders/subscribe', { path, subscribed })
export function getLabels(): Promise<{ ok: boolean; labels: MailLabel[] }> {
  return api('/labels')
}

// ── Mail ↔ Hub interconnect: route a text selection into other hub systems.
// These hit the hub's own (non-/api/mail) endpoints the SPA already uses.
export async function addTodo(title: string, source: { kind?: string; url?: string } = { kind: 'mail' }): Promise<{ ok?: boolean }> {
  const r = await fetch('/api/todos', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: title.slice(0, 200), source }) })
  if (!r.ok) throw new Error(`to-do failed (HTTP ${r.status})`)
  return r.json()
}
export async function hermesDispatch(query: string, sessionName = 'mail'): Promise<{ ok?: boolean }> {
  const r = await fetch('/api/hermes/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query, session_name: sessionName }) })
  if (!r.ok) throw new Error(`Hermes unavailable (HTTP ${r.status})`)
  return r.json()
}
export function saveLabels(labels: MailLabel[]): Promise<{ ok: boolean; labels: MailLabel[] }> {
  return api('/labels', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ labels }) })
}
/** Advanced search filters shared by the per-account and unified list endpoints.
 * (Unread is a client-side chip on the loaded window, so it's not a server filter.) */
export interface MailFilters {
  search?: string
  from?: string
  since?: string // ISO date (on/after)
  before?: string // ISO date (strictly before)
  hasAttachment?: boolean
}
function appendFilters(qs: URLSearchParams, f: MailFilters): void {
  if (f.search) qs.set('search', f.search)
  if (f.from) qs.set('from', f.from)
  if (f.since) qs.set('since', f.since)
  if (f.before) qs.set('before', f.before)
  if (f.hasAttachment) qs.set('hasAttachment', '1')
}
export function listMessages(
  account: string,
  opts: { folder?: string; limit?: number } & MailFilters = {}
): Promise<{ ok: boolean; folder: string; messages: MessageSummary[] }> {
  const qs = new URLSearchParams()
  if (opts.folder) qs.set('folder', opts.folder)
  if (opts.limit) qs.set('limit', String(opts.limit))
  appendFilters(qs, opts)
  return api(`/${enc(account)}/messages?${qs.toString()}`)
}
/** Unified "All" view: merge one folder ROLE across every mailbox, newest-first. */
export function listAllMessages(
  opts: { role?: string; limit?: number } & MailFilters = {}
): Promise<{ ok: boolean; role: string; messages: MessageSummary[]; errors: MailAccountError[] }> {
  const qs = new URLSearchParams()
  if (opts.role) qs.set('role', opts.role)
  if (opts.limit) qs.set('limit', String(opts.limit))
  appendFilters(qs, opts)
  return api(`/all/messages?${qs.toString()}`)
}
export function getMessage(
  account: string,
  uid: number | string,
  folder?: string
): Promise<{ ok: boolean; folder: string; message: MessageDetail }> {
  const qs = folder ? `?folder=${enc(folder)}` : ''
  return api(`/${enc(account)}/message/${enc(String(uid))}${qs}`)
}
export function attachmentUrl(
  account: string,
  uid: number | string,
  filename: string,
  folder?: string
): string {
  const qs = new URLSearchParams({ filename })
  if (folder) qs.set('folder', folder)
  return `/api/mail/${enc(account)}/message/${enc(String(uid))}/attachment?${qs.toString()}`
}
/** Upload a file to the shared blob store; returns a ref to attach on send/draft. */
export function uploadAttachment(file: File): Promise<{ ok: boolean } & MailAttachmentRef> {
  const fd = new FormData()
  fd.append('file', file)
  return api('/upload', { method: 'POST', body: fd })
}
/** URL to fetch a stored blob (authed; used for composer inline-image preview). */
export function blobUrl(sha256: string): string {
  return `/api/mail/blob/${enc(sha256)}`
}
/** Re-hydrate a draft's attachments into the blob store and return composer refs
 *  (so reopening a draft for editing keeps its files + inline images). */
export function blobifyDraftAttachments(
  account: string,
  uid: number | string,
  folder?: string,
): Promise<{ ok: boolean; attachments: MailAttachmentRef[] }> {
  const qs = folder ? `?folder=${enc(folder)}` : ''
  return api(`/${enc(account)}/message/${enc(String(uid))}/blobify${qs}`)
}
export function markSeen(
  account: string,
  uid: number | string,
  folder: string,
  seen = true
): Promise<{ ok: boolean }> {
  return api(`/${enc(account)}/message/${enc(String(uid))}/seen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, seen }),
  })
}
function action(
  account: string,
  uid: number | string,
  verb: 'star' | 'archive' | 'trash' | 'move',
  body: Record<string, unknown>
): Promise<{ ok: boolean }> {
  return api(`/${enc(account)}/message/${enc(String(uid))}/${verb}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}
export const starMessage = (account: string, uid: number | string, folder: string, on = true) =>
  action(account, uid, 'star', { folder, on })
export const archiveMessage = (account: string, uid: number | string, folder: string) =>
  action(account, uid, 'archive', { folder })
export const trashMessage = (account: string, uid: number | string, folder: string) =>
  action(account, uid, 'trash', { folder })
export const moveMessage = (account: string, uid: number | string, folder: string, toFolder: string) =>
  action(account, uid, 'move', { folder, toFolder })
/** Snooze a message: mark read + hide it from the list until wakeAt (ISO), when
 *  the runner re-surfaces it as unread. */
export function snoozeMessage(
  account: string,
  uid: number | string,
  folder: string,
  wakeAt: string,
  meta: { subject?: string; from?: string } = {},
): Promise<{ ok: boolean }> {
  return api(`/${enc(account)}/message/${enc(String(uid))}/snooze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, wakeAt, ...meta }),
  })
}
export function unsnoozeMessage(account: string, uid: number | string, folder: string): Promise<{ ok: boolean }> {
  const qs = folder ? `?folder=${enc(folder)}` : ''
  return api(`/${enc(account)}/message/${enc(String(uid))}/snooze${qs}`, { method: 'DELETE' })
}
export interface SnoozeEntry {
  id: string
  accountId: string
  uid: number
  folder: string
  subject: string
  from: string
  wakeAt: string
  snoozedAt: string
}
export function getSnoozed(): Promise<{ ok: boolean; snoozes: SnoozeEntry[] }> {
  return api('/snoozed')
}
export function bulkAction(
  act: BulkAction,
  items: BulkItem[],
  extra: { toFolder?: string; on?: boolean } = {}
): Promise<{ ok: boolean; action: string; results: { ok: boolean }[] }> {
  return api('/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: act, items, ...extra }),
  })
}
export function saveDraft(
  account: string,
  spec: DraftSpec
): Promise<{ ok: boolean; folder: string; uid: number | null }> {
  return api(`/${enc(account)}/draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(spec),
  })
}
export function updateDraft(
  account: string,
  uid: number | string,
  spec: DraftSpec & { folder?: string }
): Promise<{ ok: boolean; folder: string; uid: number | null }> {
  return api(`/${enc(account)}/draft/${enc(String(uid))}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(spec),
  })
}
export function deleteDraft(
  account: string,
  uid: number | string,
  folder?: string
): Promise<{ ok: boolean }> {
  const qs = folder ? `?folder=${enc(folder)}` : ''
  return api(`/${enc(account)}/draft/${enc(String(uid))}${qs}`, { method: 'DELETE' })
}
export function sendMail(
  account: string,
  spec: DraftSpec
): Promise<{ ok: boolean; messageId: string; accepted: string[] }> {
  return api(`/${enc(account)}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(spec),
  })
}
export function rewriteDraft(
  account: string,
  body: { engine?: RewriteEngine; draft: Pick<DraftSpec, 'subject' | 'text' | 'html'>; instruction: string; collabSessionId?: string }
): Promise<{ ok: boolean; engine: string; proposal: string; usedSession: boolean }> {
  return api(`/${enc(account)}/rewrite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}
export function searchCollab(
  q: string,
  limit = 8
): Promise<{ ok: boolean; query: string; results: CollabSearchHit[] }> {
  const qs = new URLSearchParams({ q })
  if (limit) qs.set('limit', String(limit))
  return api(`/collab/search?${qs.toString()}`)
}

// ── settings + signatures (Wave 2) ─────────────────────────────────────────────
export interface MailAppPrefs {
  density: 'comfortable' | 'cozy' | 'compact'
  threaded: boolean
  readingPane: 'right' | 'bottom' | 'off'
  snippets: boolean
  defaultAccount: string
  undoSendDelay: number
  markReadAfter: number
  externalImages: string
  trackingProtection: boolean
  automationEnabled?: boolean
}
export interface MailboxConfig {
  displayName: string
  replyTo: string
  aliases: string[]
  sigNew: string
  sigReply: string
  defaultFolder: string
  defaultReply: string
  autoReply: { on: boolean; subject: string; message: string }
  notify: { desktop: boolean; sound: boolean }
}
export interface MailSettings {
  app: MailAppPrefs
  mailboxes: Record<string, MailboxConfig>
  blocked: string[]
  safe: string[]
}
export interface Signature {
  id: string
  name: string
  html: string
  text: string
}
export type SettingsPatch = {
  app?: Partial<MailAppPrefs>
  mailboxes?: Record<string, Partial<MailboxConfig>>
  blocked?: string[]
  safe?: string[]
}

export function getSettings(): Promise<{ ok: boolean } & MailSettings> {
  return api('/settings')
}
export function saveSettings(patch: SettingsPatch): Promise<{ ok: boolean }> {
  return api('/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
}
export function getSignatures(): Promise<{ ok: boolean; signatures: Signature[] }> {
  return api('/signatures')
}
export interface MailRule {
  id: string
  name: string
  enabled: boolean
  conditions: { match: 'all' | 'any'; account?: string; from?: string[]; to?: string[]; subjectHas?: string[] }
  actions: { markRead?: boolean; star?: boolean; archive?: boolean; trash?: boolean; skipInbox?: boolean; moveTo?: string; forwardTo?: string[]; label?: string[] }
}
export interface ScheduledSend {
  id: string
  accountId: string
  to: string
  cc?: string
  subject: string
  text: string
  sendAt: string
  createdAt?: string
}
export function getRules(): Promise<{ ok: boolean; rules: MailRule[] }> {
  return api('/rules')
}
export function saveRules(rules: MailRule[]): Promise<{ ok: boolean; rules: MailRule[] }> {
  return api('/rules', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rules }) })
}
export function getScheduled(): Promise<{ ok: boolean; scheduled: ScheduledSend[] }> {
  return api('/scheduled')
}
export function scheduleSend(item: { accountId: string; to: string; cc?: string; subject: string; text: string; sendAt: string }): Promise<{ ok: boolean; item: ScheduledSend }> {
  return api('/scheduled', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(item) })
}
export function cancelScheduled(id: string): Promise<{ ok: boolean }> {
  return api(`/scheduled/${enc(id)}`, { method: 'DELETE' })
}
export function saveSignatures(signatures: Signature[]): Promise<{ ok: boolean; signatures: Signature[] }> {
  return api('/signatures', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ signatures }) })
}

/** Templates / canned responses — reusable message bodies (optional subject). */
export interface MailTemplate {
  id: string
  name: string
  subject: string
  html: string
  text: string
}
export function getTemplates(): Promise<{ ok: boolean; templates: MailTemplate[] }> {
  return api('/templates')
}
export function saveTemplates(templates: MailTemplate[]): Promise<{ ok: boolean; templates: MailTemplate[] }> {
  return api('/templates', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ templates }) })
}

// ── hooks ─────────────────────────────────────────────────────────────────────
export const FOLDER_ORDER: FolderRole[] = ['inbox', 'drafts', 'sent', 'archive', 'junk', 'trash', 'other']

/** Load the account list once (after enroll). */
export function useMailAccounts(): { accounts: MailAccount[]; error: string | null; loading: boolean } {
  const [accounts, setAccounts] = useState<MailAccount[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let alive = true
    listAccounts()
      .then((r) => alive && setAccounts(r.accounts || []))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])
  return { accounts, error, loading }
}

export interface NewMailEvent {
  account: string
  folder: string
  count: number
}
/** Subscribe to /api/mail/events for live unread counts + new-mail pushes.
 *  `adjustCount` applies an optimistic local delta (e.g. -1 when a mail is read)
 *  that the next authoritative `counts` event reconciles. */
export function useMailEvents(
  opts: { onNewMail?: (e: NewMailEvent) => void } = {},
): { counts: Record<string, number>; connected: boolean; adjustCount: (account: string, delta: number) => void } {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [connected, setConnected] = useState(false)
  // Keep the latest callback in a ref so the SSE subscription never re-mounts.
  const onNewRef = useRef(opts.onNewMail)
  onNewRef.current = opts.onNewMail
  useEffect(() => {
    let es: EventSource | null = null
    let closed = false
    enrollMail().then(() => {
      if (closed) return
      es = new EventSource('/api/mail/events')
      es.addEventListener('hello', () => setConnected(true))
      es.addEventListener('counts', (e) => {
        try {
          setCounts(JSON.parse((e as MessageEvent).data) as Record<string, number>)
        } catch {
          /* ignore */
        }
      })
      es.addEventListener('mail:new', (e) => {
        try {
          onNewRef.current?.(JSON.parse((e as MessageEvent).data) as NewMailEvent)
        } catch {
          /* ignore */
        }
      })
      es.onerror = () => setConnected(false)
    })
    return () => {
      closed = true
      es?.close()
    }
  }, [])
  const adjustCount = useCallback((account: string, delta: number) => {
    setCounts((c) => ({ ...c, [account]: Math.max(0, (c[account] || 0) + delta) }))
  }, [])
  return { counts, connected, adjustCount }
}
